import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Textarea } from '@lightning/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import {
  ONBOARDING_ANSWER_MAX,
  clearOnboardingDraft,
  onboardingDraftKey,
  saveOnboardingDraft,
} from '../lib/onboardingDraft';
import { markOnboardingResolved } from '../lib/onboardingStatus';
import { supabase } from '../services/supabase';

const QUESTIONS = [
  'How do you show up?',
  'How would others describe you?',
  'How would you like to show up?',
] as const;

type Answers = [string, string, string];
type Phase = 'questions' | 'pause' | 'quote';

interface SavedProgress {
  step: number;
  answers: Answers;
}

const emptyAnswers = (): Answers => ['', '', ''];
function readProgress(userId: string): SavedProgress {
  try {
    const raw = localStorage.getItem(onboardingDraftKey(userId));
    if (!raw) return { step: 0, answers: emptyAnswers() };
    const parsed = JSON.parse(raw) as Partial<SavedProgress>;
    const answers = Array.isArray(parsed.answers)
      ? QUESTIONS.map((_, index) => String(parsed.answers?.[index] || '')) as Answers
      : emptyAnswers();
    return {
      step: Math.max(0, Math.min(Number(parsed.step) || 0, QUESTIONS.length - 1)),
      answers,
    };
  } catch {
    return { step: 0, answers: emptyAnswers() };
  }
}

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const initialProgress = useMemo(
    () => user ? readProgress(user.id) : { step: 0, answers: emptyAnswers() },
    [user],
  );
  const [step, setStep] = useState(initialProgress.step);
  const [answers, setAnswers] = useState<Answers>(initialProgress.answers);
  const [phase, setPhase] = useState<Phase>('questions');
  const [checkingStatus, setCheckingStatus] = useState(Boolean(user));
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusAttempt, setStatusAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentAnswer = answers[step];

  useEffect(() => {
    if (!user) {
      setCheckingStatus(false);
      return;
    }

    let cancelled = false;
    setCheckingStatus(true);
    setStatusError(null);

    const checkStatus = async () => {
      const { data, error: statusQueryError } = await supabase
        .from('howie_onboarding_reflections')
        .select('completed_at, skipped_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (cancelled) return;
      if (statusQueryError) {
        setStatusError('We could not check your onboarding status. Please try again.');
        setCheckingStatus(false);
        return;
      }

      if (data?.completed_at || data?.skipped_at) {
        markOnboardingResolved(user.id);
        clearOnboardingDraft(user.id);
        navigate('/chat', { replace: true });
        return;
      }

      setCheckingStatus(false);
    };

    void checkStatus();
    return () => {
      cancelled = true;
    };
  }, [navigate, statusAttempt, user]);

  useEffect(() => {
    if (!user || checkingStatus || statusError || phase !== 'questions') return;
    saveOnboardingDraft(user.id, JSON.stringify({ step, answers }));
  }, [answers, checkingStatus, phase, statusError, step, user]);

  useEffect(() => {
    if (phase === 'questions') textareaRef.current?.focus();
  }, [step, phase]);

  useEffect(() => {
    if (phase !== 'pause') return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const timer = window.setTimeout(() => setPhase('quote'), reduceMotion ? 0 : 1100);
    return () => window.clearTimeout(timer);
  }, [phase]);

  const updateCurrentAnswer = (value: string) => {
    setAnswers((current) => {
      const next = [...current] as Answers;
      next[step] = value;
      return next;
    });
  };

  const completeReflection = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('howie_onboarding_reflections')
        .upsert({
          user_id: user.id,
          version: 1,
          completed_at: new Date().toISOString(),
          skipped_at: null,
          how_i_show_up: answers[0].trim(),
          how_others_describe_me: answers[1].trim(),
          how_i_want_to_show_up: answers[2].trim(),
        }, { onConflict: 'user_id' });
      if (updateError) throw updateError;

      markOnboardingResolved(user.id);
      clearOnboardingDraft(user.id);
      posthog?.capture('howie_onboarding_completed', { question_count: QUESTIONS.length });
      setPhase('pause');
    } catch (caughtError) {
      console.error('Unable to save Howie onboarding:', caughtError);
      setError('We could not save your reflection. Your words are still here—please try again.');
    } finally {
      setSaving(false);
    }
  };

  const skipReflection = async () => {
    if (!user || saving) return;
    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('howie_onboarding_reflections')
        .upsert({
          user_id: user.id,
          version: 1,
          skipped_at: new Date().toISOString(),
        }, { onConflict: 'user_id', ignoreDuplicates: true });
      if (updateError) throw updateError;

      markOnboardingResolved(user.id);
      clearOnboardingDraft(user.id);
      posthog?.capture('howie_onboarding_skipped', { after_step: step });
      setPhase('pause');
    } catch (caughtError) {
      console.error('Unable to skip Howie onboarding:', caughtError);
      setError('We could not continue just yet. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = () => {
    if (!currentAnswer.trim()) return;
    posthog?.capture('howie_onboarding_step_completed', { step: step + 1 });
    if (step < QUESTIONS.length - 1) {
      setStep((current) => current + 1);
      setError(null);
      return;
    }
    void completeReflection();
  };

  if (checkingStatus) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-how-paper-soft px-6">
        <p role="status" className="text-sm text-how-text-secondary">Preparing your reflection…</p>
      </main>
    );
  }

  if (statusError) {
    return (
      <main className="flex min-h-svh flex-col items-center justify-center gap-4 bg-how-paper-soft px-6 text-center">
        <p role="alert" className="text-sm text-how-text-secondary">{statusError}</p>
        <button
          type="button"
          onClick={() => setStatusAttempt((attempt) => attempt + 1)}
          className="rounded-full bg-how-space-deep px-6 py-2.5 text-sm text-white transition-colors hover:bg-how-space-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2"
        >
          Try again
        </button>
      </main>
    );
  }

  if (phase === 'pause') {
    return (
      <main className="flex min-h-svh items-center justify-center bg-how-paper-soft">
        <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-how-accent-warm" aria-hidden="true" />
        <p role="status" className="sr-only">Take a moment</p>
      </main>
    );
  }

  if (phase === 'quote') {
    return (
      <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-how-space-deep px-6 py-12 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{ background: 'radial-gradient(circle at 50% 45%, var(--how-space-light) 0%, transparent 58%)' }}
          aria-hidden="true"
        />
        <section className="relative z-10 mx-auto max-w-2xl text-center">
          <blockquote
            className="text-[1.65rem] leading-[1.4] tracking-[-0.012em] text-white sm:text-[2.15rem] sm:leading-[1.32]"
            style={{ fontFamily: 'var(--font-voice)', fontWeight: 400 }}
          >
            <span className="block overflow-hidden py-0.5">
              <span className="quote-line-reveal block" style={{ animationDelay: '700ms' }}>
                “Start your journey with a pause—
              </span>
            </span>
            <span className="block overflow-hidden py-0.5">
              <span className="quote-line-reveal block" style={{ animationDelay: '1250ms' }}>
                and in that pause,
              </span>
            </span>
            <span className="block overflow-hidden py-0.5">
              <span className="quote-line-reveal block" style={{ animationDelay: '1800ms' }}>
                hear your call of leadership.”
              </span>
            </span>
          </blockquote>
          <p
            className="quote-fade-reveal mt-8 text-xs tracking-[0.16em] text-white/60 uppercase"
            style={{ animationDelay: '2700ms' }}
          >
            — Dov Seidman
          </p>
          <button
            type="button"
            onClick={() => navigate('/chat', { replace: true })}
            className="quote-fade-reveal mt-12 rounded-full bg-how-accent-warm px-7 py-3 text-sm text-how-space-deep transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-how-space-deep"
            style={{ animationDelay: '3400ms' }}
          >
            Begin
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="relative flex min-h-svh flex-col overflow-x-hidden bg-how-paper-soft px-5 py-8 text-how-space-deep sm:px-8">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[42%] opacity-55"
        style={{ background: 'linear-gradient(180deg, transparent 0%, var(--how-rose) 100%)' }}
        aria-hidden="true"
      />

      <header className="relative z-10 grid grid-cols-[1fr_auto_1fr] items-center">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep((current) => current - 1)}
            className="inline-flex h-9 min-w-16 items-center justify-start gap-1.5 rounded-sm text-xs text-how-text-secondary transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
        ) : <span aria-hidden="true" />}
        <img src="/logo_green.svg" alt="Howie" className="h-7 w-auto justify-self-center" />
        <button
          type="button"
          onClick={() => void skipReflection()}
          disabled={saving}
          className="inline-flex h-9 min-w-16 items-center justify-end justify-self-end rounded-sm text-xs text-how-text-secondary transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep disabled:opacity-40"
        >
          Skip
        </button>
      </header>

      <section className="relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col justify-center py-6">
        <div
          role="progressbar"
          aria-label="Onboarding progress"
          aria-valuemin={1}
          aria-valuemax={QUESTIONS.length}
          aria-valuenow={step + 1}
          className="mx-auto mb-6 flex w-20 gap-1.5"
        >
          {QUESTIONS.map((_, index) => (
            <span
              key={index}
              className={`h-px flex-1 rounded-full transition-colors duration-500 ${
                index <= step ? 'bg-how-accent-warm' : 'bg-how-space-deep/15'
              }`}
              aria-hidden="true"
            />
          ))}
        </div>

        <div key={step} className="animate-fade-in-up">
          <h1
            className="text-center text-[1.7rem] leading-[1.12] tracking-[-0.02em] sm:text-[1.9rem] md:whitespace-nowrap md:text-[2.15rem]"
            style={{ fontFamily: 'var(--font-voice)', fontWeight: 400 }}
          >
            {QUESTIONS[step]}
          </h1>

          <Textarea
            ref={textareaRef}
            value={currentAnswer}
            onChange={(event) => updateCurrentAnswer(event.target.value)}
            maxLength={ONBOARDING_ANSWER_MAX}
            placeholder="Write what comes to mind…"
            aria-label={QUESTIONS[step]}
            className="mt-6 min-h-28 resize-none rounded-2xl border-how-space-deep/15 bg-white/80 px-4 py-4 font-body text-base leading-7 text-how-space-deep shadow-card placeholder:text-how-text-secondary/45 focus-visible:ring-how-space-deep"
          />

          {error && <p role="alert" className="mt-4 text-sm text-how-space-mid">{error}</p>}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleContinue}
              disabled={!currentAnswer.trim() || saving}
              className="rounded-full bg-how-space-deep px-7 py-3 text-sm text-white transition-colors hover:bg-how-space-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-35"
            >
              {saving ? 'Saving…' : step === QUESTIONS.length - 1 ? 'Complete' : 'Continue'}
            </button>
          </div>
        </div>
      </section>

      <footer className="relative z-10 pt-4 text-center text-xs leading-5 text-how-text-secondary/65">
        Your reflections are saved privately to your account.
      </footer>
    </main>
  );
}
